import React, { useState, useEffect, useRef } from "react";
import { Trans, useTranslation } from "react-i18next";
import Footer from "../Common/Footer";
import countryFoodPreferences from "../../utils/country_food_preferences.json";
import { getUserCountrySync } from "../../services/apiService";
import {
  trackLandingViewed,
  trackCTAClicked,
  CTA_LOCATION,
} from "../../analytics";
import SavingsCalculator from "./SavingsCalculator";
import { CalendarDays, Heart, Gauge } from "lucide-react";

// Variant marker so analytics can distinguish this A/B page from the live landing
const VARIANT = "testb";

// Inline styles for transitions (shared with the live landing aesthetic)
const styles = `
  .hero-element {
    opacity: 1;
    transform: translateY(0);
    transition: opacity 136ms cubic-bezier(0.25, 0.46, 0.45, 0.94),
                transform 136ms cubic-bezier(0.25, 0.46, 0.45, 0.94);
  }

  .hero-element.exiting {
    opacity: 0;
    transform: translateY(-10px);
  }

  .hero-title { transition-delay: 0ms; }
  .hero-description { transition-delay: 26ms; }
  .hero-image { transition-delay: 51ms; }
  .hero-button { transition-delay: 77ms; }
  .hero-rotating-text { transition-delay: 102ms; }
  .hero-how-it-works { transition-delay: 128ms; }
  .hero-reviews { transition-delay: 153ms; }

  /* Fixed bottom CTA */
  .fixed-bottom-cta {
    position: fixed;
    bottom: 12px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 50;
    transition: transform 300ms ease;
    white-space: nowrap;
  }
  .fixed-bottom-cta.show {
    transform: translateX(-50%) translateY(0);
    opacity: 1;
  }
  .fixed-bottom-cta.hide {
    transform: translateX(-50%) translateY(200%);
  }

  /* Stacked recipe card effect */
  .stacked-card { position: relative; }
  .stacked-card-bg {
    position: absolute;
    top: 0; left: 0; width: 100%; height: 100%;
    border-radius: 12px;
    border: 0.5px solid rgba(255, 255, 255, 0.8);
    background: #F8F8F7;
    pointer-events: none;
    box-shadow: 0px 1px 4px 1px rgba(35, 33, 22, 0.06);
    opacity: 0.8;
  }
  .stacked-card > .stacked-card-bg:first-child {
    transform: translate(10px, 10px);
    background: #E8E8E6;
    z-index: 1;
  }
  .stacked-card > .stacked-card-bg:nth-child(2) {
    transform: translate(5px, 5px);
    background: #F0F0EE;
    z-index: 2;
  }

  .scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
  .scrollbar-hide::-webkit-scrollbar { display: none; }

  .hero-section { min-height: 100dvh; }
  @media (min-width: 768px) {
    .hero-section {
      min-height: auto;
      padding-top: 64px;
      padding-bottom: 64px;
    }
  }

  /* Honor users who prefer reduced motion */
  @media (prefers-reduced-motion: reduce) {
    .hero-element,
    .hero-element.exiting,
    .fixed-bottom-cta {
      transition: none !important;
    }
  }
`;

const LandingPage = ({ onStart }) => {
  const { t } = useTranslation("pages");
  const [currentDescriptionIndex, setCurrentDescriptionIndex] = useState(0);
  const [isExiting, setIsExiting] = useState(false);

  // Reuse the already-translated rotating descriptions from the live landing
  const rotatingDescriptions = t("landing.rotatingDescriptions", {
    returnObjects: true,
  });

  // Testimonials (placeholder copy — replace with real customer quotes before launch)
  const reviewItems = t("landingB.reviews.items", { returnObjects: true });
  const reviews = Array.isArray(reviewItems) ? reviewItems : [];

  // CTA state/refs
  const ctaContainerRef = useRef(null);
  const footerRef = useRef(null);
  const [ctaHiddenByFooter, setCtaHiddenByFooter] = useState(false);
  const [showFixedBottomCta, setShowFixedBottomCta] = useState(false);

  // FAQ accordion state
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const toggleFaq = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  // Pause the rotating hero copy on hover/focus (WCAG 2.2.2) and when the
  // user prefers reduced motion.
  const [isRotationPaused, setIsRotationPaused] = useState(false);
  const prefersReducedMotion =
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // Track Landing Viewed on mount (variant tagged via utm_content fallback)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    trackLandingViewed({
      referrer: document.referrer,
      utm_source: urlParams.get("utm_source"),
      utm_medium: urlParams.get("utm_medium"),
      utm_campaign: urlParams.get("utm_campaign"),
      utm_content: urlParams.get("utm_content") || VARIANT,
      utm_term: urlParams.get("utm_term"),
    });
  }, []);

  // Rotate description every 5 seconds — paused on hover/focus and disabled
  // entirely when the user prefers reduced motion (WCAG 2.2.2).
  useEffect(() => {
    if (prefersReducedMotion || isRotationPaused) {
      return;
    }
    const interval = setInterval(() => {
      setCurrentDescriptionIndex(
        (prev) => (prev + 1) % rotatingDescriptions.length
      );
    }, 5000);
    return () => clearInterval(interval);
  }, [rotatingDescriptions.length, prefersReducedMotion, isRotationPaused]);

  // Hide CTA when footer is visible
  useEffect(() => {
    const footerEl = footerRef.current;
    if (!footerEl || !("IntersectionObserver" in window)) {
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        setCtaHiddenByFooter(entry.isIntersecting);
      },
      { root: null, threshold: 0 }
    );
    observer.observe(footerEl);
    return () => observer.disconnect();
  }, []);

  // Track hero CTA visibility for fixed bottom CTA
  useEffect(() => {
    const ctaEl = ctaContainerRef.current;
    if (!ctaEl || !("IntersectionObserver" in window)) {
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        const hasScrolled = window.scrollY > 100;
        setShowFixedBottomCta(!entry.isIntersecting && hasScrolled);
      },
      { root: null, threshold: 0, rootMargin: "0px 0px -100px 0px" }
    );
    observer.observe(ctaEl);
    return () => observer.disconnect();
  }, []);

  // Handle start wizard with exit animation
  const handleStart = (ctaLocation = CTA_LOCATION.HERO) => {
    trackCTAClicked({
      cta_location: ctaLocation,
      cta_text: VARIANT,
    });

    setIsExiting(true);
    setTimeout(() => {
      onStart();
    }, 204);
  };

  const userCountry = getUserCountrySync();
  const currencySymbol =
    countryFoodPreferences?.[userCountry]?.currencySymbol ||
    countryFoodPreferences?.Spain?.currencySymbol ||
    "$";

  return (
    <>
      <style>{styles}</style>
      <div className="flex flex-col justify-between overflow-x-hidden">
        {/* Hero Section */}
        <section className="hero-section flex overflow-hidden transition-all duration-300 ease-in-out justify-center items-center">
          <div className="relative z-10 container mx-auto pt-10 pb-8 md:py-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-center md:gap-10 max-w-5xl mx-auto">
              {/* Text Content */}
              <div className="md:flex-1 md:max-w-xl flex flex-col">
                <div
                  className={`max-w-5xl mx-auto md:mx-0 text-center px-6 hero-element hero-title order-1 ${
                    isExiting ? "exiting" : ""
                  }`}
                >
                  <div className="content-stretch flex flex-col items-center relative shrink-0">
                    <div className="content-stretch flex items-center justify-center relative shrink-0 w-full">
                      <h1 className="leading-[52px] md:leading-[60px] font-extrabold relative text-[#0d0c07] md:text-[42px] text-[32px] tracking-[-0.3328px]">
                        {t("landingB.hero.title")}
                      </h1>
                    </div>
                    <div className="content-stretch flex items-center justify-center relative shrink-0 w-full">
                      <p className="leading-[44px] md:leading-[52px] italic font-bold relative shrink-0 text-[#0d0c07] md:text-[36px] text-[26px] tracking-[-0.3328px]">
                        {t("landingB.hero.tagline")}
                      </p>
                    </div>
                  </div>

                  <div className="content-stretch flex items-start justify-start relative shrink-0 w-full md:mt-6 mt-4">
                    <p className="leading-[32px] relative text-center text-[#0d0c07] text-[16.4px] w-full">
                      <Trans
                        i18nKey="landingB.hero.description"
                        t={t}
                        components={{ bold: <span className="font-bold" /> }}
                      />
                    </p>
                  </div>
                </div>

                {/* Food Image - mobile */}
                <div
                  className={`relative md:mt-2 mt-0 flex justify-center md:hidden hero-element hero-image order-2 ${
                    isExiting ? "exiting" : ""
                  }`}
                >
                  <div className="relative">
                    <div className="flex items-center justify-center relative">
                      <div className="text-8xl px-8">
                        <img
                          src="/assets/hero-banner-image.png"
                          alt={t("landingB.hero.imageAlt")}
                          loading="eager"
                          decoding="async"
                          fetchpriority="high"
                          className="mx-auto mb-2 w-auto h-auto max-w-full max-h-[272px] md:max-h-[400px]"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* CTA Button */}
                <div
                  ref={ctaContainerRef}
                  className={`max-w-5xl mx-auto md:mx-0 text-center md:text-center px-6 md:mt-8 mt-3 hero-element hero-button order-3 ${
                    isExiting ? "exiting" : ""
                  }`}
                >
                  <button
                    className="
                        inline-flex justify-center items-center gap-1
                        h-[56px] px-[40px] py-[14px] mb-6
                        rounded-[32px]
                        border-[1px] border-[linear-gradient(200deg,_#090_0%,_#060_100%)]
                        bg-[#090]
                        text-[#FFF] text-center
                        text-[14px] font-medium leading-[28px]
                        transition-colors
                        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#0d0c07]
                        md:hover:border-[0.5px] md:hover:border-[#060]
                        md:hover:bg-[#060]
                        bg-[linear-gradient(200deg,_#090_0%,_#060_100%)]
                        shadow-[0_2px_16px_0_rgba(0,0,0,0.12)_inset,_0_0_32px_0_rgba(255,255,255,0.4)_inset,_0_0px_0px_2px_rgba(0,102,0,0.4)]
                      "
                    onClick={handleStart}
                  >
                    <span>{t("landingB.hero.cta")}</span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M11.3816 5.54742C11.702 5.22734 12.2089 5.20753 12.5525 5.48785L12.6189 5.54742L18.4519 11.3814C18.7936 11.7231 18.7936 12.277 18.4519 12.6187L12.6189 18.4517L12.5525 18.5123C12.2088 18.7925 11.7019 18.7721 11.3816 18.4517C11.0399 18.11 11.0399 17.5561 11.3816 17.2144L15.7214 12.8746H6.16675C5.6835 12.8746 5.29175 12.4828 5.29175 11.9996C5.29192 11.5165 5.68361 11.1246 6.16675 11.1246H15.7205L11.3816 6.7857L11.321 6.7193C11.0406 6.37562 11.0612 5.86782 11.3816 5.54742Z"
                        fill="white"
                      />
                    </svg>
                  </button>
                </div>

                <div
                  className={`max-w-5xl mx-auto md:mx-0 text-center md:text-center px-6 hero-element mb-6 hero-rotating-text order-4 ${
                    isExiting ? "exiting" : ""
                  }`}
                  onMouseEnter={() => setIsRotationPaused(true)}
                  onMouseLeave={() => setIsRotationPaused(false)}
                  onFocusCapture={() => setIsRotationPaused(true)}
                  onBlurCapture={() => setIsRotationPaused(false)}
                >
                  <div className="text-[var(--Color-Grays-Gray-10,#0D0C07)] text-center md:text-center text-[16px] font-normal leading-[24px] max-w-3xl">
                    <p className="font-semibold md:mb-2 mb-0" aria-live="polite">
                      {rotatingDescriptions[currentDescriptionIndex]}
                    </p>
                    <p className="mb-0 md:text-[14px] text-[12px]">
                      {t("landingB.hero.savings")}
                    </p>
                  </div>
                </div>
              </div>

              {/* Food Image - desktop */}
              <div
                className={`relative hidden md:flex justify-center md:flex-1 hero-element hero-image ${
                  isExiting ? "exiting" : ""
                }`}
              >
                <div className="relative">
                  <div className="flex items-center justify-center relative">
                    <div className="text-8xl px-8 md:px-0">
                      <img
                        src="/assets/hero-banner-image.png"
                        alt={t("landingB.hero.imageAlt")}
                        width="780"
                        height="600"
                        loading="eager"
                        decoding="async"
                        className="mx-auto max-w-full max-h-[260px] md:max-h-[400px]"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Philosophy — the app adapts to you */}
        <section className="bg-[#F3F3F2] shadow-[inset_0_12px_32px_0_rgba(0,0,0,0.04)] py-16 md:py-24 relative overflow-hidden">
          <div className="max-w-5xl mx-auto px-10 md:px-6 relative md:pb-0">
            <div className="w-full flex pl-0 md:pl-[10%] max-w-5xl flex-col items-left text-left">
              <div className="max-w-2xl mb-10">
                <span className="inline-block bg-[#FFCB00] text-[#0d0c07] text-[12px] font-bold uppercase tracking-[0.08em] rounded-[6px] px-2 py-1 mb-3">
                  {t("landingB.philosophy.eyebrow")}
                </span>

                <h2 className="text-[#0d0c07] max-w-[320px] md:max-w-5xl md:text-[38px] text-[32px] leading-[56px] tracking-[-0.32px] font-extrabold">
                  <span>{t("landingB.philosophy.title")}</span>
                </h2>
                <h3 className="font-bold text-[#0d0c07] italic text-[24px] leading-[40px] mt-1 mb-4">
                  {t("landingB.philosophy.subtitle")}
                </h3>
                <p className="text-[14.8px] text-[#4d4b42] leading-[28px]">
                  {t("landingB.philosophy.intro")}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
              {[
                { n: 1, Icon: CalendarDays },
                { n: 2, Icon: Heart },
                { n: 3, Icon: Gauge },
              ].map(({ n, Icon }) => (
                <div
                  key={n}
                  className="p-5 rounded-[12px] border-[0.5px] border-[#ffffff] bg-white shadow-[0px_1px_2px_0px_rgba(35,33,22,0.04),_0px_6px_12px_0px_rgba(83,81,69,0.04)] flex flex-col gap-2"
                >
                  <div className="bg-[#ffd429] rounded-[12px] w-11 h-11 flex items-center justify-center mb-1">
                    <Icon size={24} strokeWidth={2} className="text-[#0d0c07]" aria-hidden="true" />
                  </div>
                  <h3 className="font-semibold text-[18px] text-[#0d0c07] leading-[28px]">
                    {t(`landingB.philosophy.item${n}Title`)}
                  </h3>
                  <p className="text-[14.8px] text-[#4d4b42] leading-[24px]">
                    {t(`landingB.philosophy.item${n}Desc`)}
                  </p>
                </div>
              ))}
            </div>
            <div className="w-full md:px-6 pl-0 px-10 flex md:pl-[10%] max-w-5xl md:pr-24 flex-col items-left text-left mb-2">
              <p className="font-bold text-[#0d0c07] text-[18px] leading-[32px] mt-10 max-w-2xl">
                {t("landingB.philosophy.closing")}
              </p>
            </div>
          </div>
          
        </section>

        {/* Savings Calculator — concrete value, placed high */}
        <SavingsCalculator
          onStart={handleStart}
          currencySymbol={currencySymbol}
        />

        {/* The Hard Part (reuses already-translated copy) */}
        <section className="bg-[#F3F3F2] flex flex-col gap-8 items-center pt-16 md:pt-20 px-0 relative w-full">
          <div className="w-full md:px-6 px-10 flex md:pl-[10%] max-w-5xl flex-col items-left text-left">
            <h2 className="text-[#0d0c07] max-w-[320px] md:max-w-5xl md:text-[40px] text-[28px] md:leading-[56px] leading-[44px] md:mb-1 mb-1 tracking-[-0.32px] font-extrabold">
              <span>{t("landing.section2.titlePart1")} </span>
              <span className="text-[#090]">
                {t("landing.section2.titleHighlight")}
              </span>
            </h2>
            <h3 className="font-bold text-[#0d0c07] italic md:text-[28px] text-[24px] md:leading-[40px] leading-[32px] mt-1 mb-2">
              {t("landing.section2.subtitle")}
            </h3>
          </div>

          <div className="w-full md:px-6 px-10 flex justify-center max-w-5xl">
            <div className="flex flex-col gap-3 w-full md:grid md:grid-cols-3 md:gap-6">
              {[
                { img: "/assets/landing/intro-1.jpg", key: "title1" },
                { img: "/assets/landing/intro-2.jpg", key: "title2" },
                { img: "/assets/landing/intro-3.jpg", key: "title3" },
              ].map((f) => (
                <div
                  key={f.key}
                  className="flex items-start md:flex-col md:items-stretch"
                >
                  <div className="p-4 gap-3 overflow-hidden rounded-[12px] border-[0.5px] border-[#ffffff] bg-white shadow-[0px_1px_2px_0px_rgba(35,33,22,0.04),_0px_6px_12px_0px_rgba(83,81,69,0.04)] flex flex-col">
                    <div className="relative w-full overflow-hidden">
                      <img
                        src={f.img}
                        alt={t(`landing.section2.copy.${f.key}`)}
                        width="800"
                        height="533"
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover rounded-[6px] aspect-[5/2.5] md:aspect-[5/3]"
                      />
                    </div>
                    <div className="flex gap-3">
                      <div className="flex flex-col items-center">
                        <h3 className="font-semibold items-center text-[#0D0C07] text-[15.2px] leading-[28px]">
                          {t(`landing.section2.copy.${f.key}`)}
                        </h3>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="w-full md:px-6 px-10 flex md:pl-[10%] max-w-5xl md:pr-24 flex-col items-left text-left mb-2 pb-16 md:pb-20">
            <p className="font-medium text-[#0d0c07] italic text-[18px] leading-[32px] mt-2">
              {t("landing.section2.copy.description")}
            </p>
            <p className="font-bold text-[#0d0c07] text-[26px] leading-[40px] mt-3">
              {t("landing.section2.copy.solution2")}
            </p>
            <p className="font-extrabold text-[#B50B0B] text-[26px] leading-[40px] mt-2">
              {t("landing.section2.copy.solution1")}
            </p>
          </div>
        </section>

        {/* Secondary Banner Section (reuses already-translated copy) */}
        <section
          className="relative w-full overflow-hidden"
          data-name="Secondary Banner"
        >
          <img
            alt=""
            className="absolute inset-0 max-w-none object-cover pointer-events-none w-full h-full"
            style={{ objectPosition: "50% 20%" }}
            src="/assets/landing/what-bg.jpg"
          />
          <div className="w-full h-full max-w-5xl mx-auto flex flex-col justify-center items-start relative">
            <div className="flex flex-col gap-[44px] items-start pt-20 pb-32 relative w-full h-full">
              <div className="flex flex-col items-start relative shrink-0 w-full">
                <div className="flex flex-col items-start relative shrink-0 w-full">
                  <div className="flex items-start relative shrink-0 w-full">
                    <h2 className="md:px-6 px-10 pr-20 basis-0 font-bold grow leading-[60px] relative shrink-0 text-[36px] md:text-[40px] text-white tracking-[-0.2304px]">
                      <Trans
                        i18nKey="landing.whatWeDo.title"
                        t={t}
                        components={{
                          yellow: <span className="text-[#ffd429]" />,
                        }}
                      />
                    </h2>
                  </div>
                </div>
              </div>

              <div className="flex flex-col items-start relative shrink-0 w-full">
                <div className="flex flex-col items-start relative shrink-0 w-full">
                  <div className="flex flex-col items-start relative shrink-0 w-full">
                    <div className="flex flex-col gap-[24px] items-start relative shrink-0 w-full">
                      {/* Line 1 */}
                      <div className="md:px-6 px-10 flex gap-[12px] items-start relative shrink-0 w-full">
                        <div className="bg-[#ffd429] flex flex-col items-center justify-center overflow-clip relative rounded-[12px] shrink-0 w-[32px] h-[32px]">
                          <div className="basis-0 grow min-h-px min-w-px relative shrink-0 w-[24px]">
                            <svg
                              className="block w-full h-full"
                              fill="none"
                              preserveAspectRatio="none"
                              viewBox="0 0 24 32"
                            >
                              <g>
                                <path
                                  d="M18.0477 10.3808C18.3893 10.0395 18.9433 10.0396 19.285 10.3808C19.6267 10.7225 19.6267 11.2773 19.285 11.619L10.118 20.7851C9.95398 20.949 9.73171 21.0418 9.49982 21.0419C9.26791 21.0418 9.04468 20.9491 8.88068 20.7851L4.71369 16.619C4.37232 16.2774 4.3722 15.7224 4.71369 15.3808C5.0553 15.0392 5.61024 15.0394 5.95197 15.3808L9.49885 18.9286L18.0477 10.3808Z"
                                  fill="#0D0C07"
                                />
                              </g>
                            </svg>
                          </div>
                        </div>
                        <div className="flex flex-col gap-[4px] flex-1 items-start justify-center min-w-0">
                          <h3 className="font-bold leading-[32px] text-[20px] text-white mb-3 md:mb-1">
                            {t("landing.whatWeDo.feature1Title")}
                          </h3>
                          <p className="font-semibold leading-[24px] text-[#e2e1df] text-[16px] mb-2">
                            {t("landing.whatWeDo.feature1Desc")}
                          </p>
                        </div>
                      </div>

                      {/* Recipes Horizontal Scroll */}
                      <div className="w-full overflow-x-auto pb-6 md:px-6 px-10 scrollbar-hide">
                        <div className="flex flex-nowrap gap-8 pr-8 pb-3">
                          {[
                            {
                              img: "recipe-1.webp",
                              badge: "breakfasts",
                              days: 4,
                              time: "15m",
                              title: "recipe1Title",
                              desc: "recipe1Desc",
                            },
                            {
                              img: "recipe-2.webp",
                              badge: "lunches",
                              days: 5,
                              time: "35m",
                              title: "recipe2Title",
                              desc: "recipe2Desc",
                            },
                            {
                              img: "recipe-3.webp",
                              badge: "dinners",
                              days: 3,
                              time: "40m",
                              title: "recipe3Title",
                              desc: "recipe3Desc",
                            },
                          ].map((r) => (
                            <div
                              key={r.img}
                              className="stacked-card min-w-[300px] overflow-visible relative"
                            >
                              <div className="stacked-card-bg"></div>
                              <div className="stacked-card-bg"></div>
                              <div className="relative z-10 p-3 md:p-4 rounded-[12px] border-[0.5px] border-[var(--Color-Grays-Gray-0,#ffffff)] bg-[var(--Color-Grays-Gray-0,#FFF)] shadow-[0px_1px_2px_0px_rgba(35,33,22,0.04),_0px_6px_12px_0px_rgba(83,81,69,0.04)] flex flex-col">
                                <div className="relative w-full overflow-hidden aspect-[2/1] rounded-[6px]">
                                  <div
                                    className="absolute inset-0"
                                    style={{
                                      backgroundImage: `linear-gradient(rgba(13, 12, 7, 0) 72%, rgba(13, 12, 7, 0.44) 100%), url("/assets/landing/${r.img}")`,
                                      backgroundSize: "cover",
                                      backgroundPosition: "50% center",
                                      backgroundRepeat: "no-repeat",
                                    }}
                                  ></div>
                                  <div className="absolute top-1 right-1">
                                    <span className="px-2 py-1 text-[#0D0C07] rounded-[6px] border-[0px] border-[#0D0C07] bg-[rgba(255,212,41,0.8)] backdrop-blur-lg font-normal text-[12px]">
                                      {t(`landing.recipeCards.${r.badge}`)}
                                    </span>
                                  </div>
                                  <div className="absolute bottom-1 left-1 flex gap-0.5">
                                    <span className="px-2 py-1 text-white rounded-[6px] border-[0px] border-[#0D0C07] bg-[rgba(37,34,24,0.64)] backdrop-blur-lg font-normal text-[10px]">
                                      <span className="text-[#FFCB00]">#</span>
                                      {t("landing.recipeCards.freshUpTo", {
                                        days: r.days,
                                      })}
                                    </span>
                                    <span className="px-2 py-1 text-white rounded-[6px] border-[0px] border-[#0D0C07] bg-[rgba(37,34,24,0.64)] backdrop-blur-lg font-normal text-[10px]">
                                      <span className="text-[#FFCB00]">#</span>
                                      {r.time}
                                    </span>
                                  </div>
                                </div>
                                <div className="pt-2 flex flex-col flex-1">
                                  <h3 className="text-[var(--Color-Grays-Gray-10,#0D0C07)] text-[14px] font-semibold leading-[28px]">
                                    {t(`landing.recipeCards.${r.title}`)}
                                  </h3>
                                  <p className="text-[var(--Color-Grays-Gray-8,#56554D)] text-[12px] font-normal leading-[22px] mb-0 line-clamp-2 flex-1">
                                    {t(`landing.recipeCards.${r.desc}`)}
                                  </p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Line 2 */}
                      <div className="md:px-6 px-10 flex gap-[12px] items-start relative shrink-0 w-full">
                        <div className="bg-[#ffd429] flex flex-col items-center justify-center overflow-clip relative rounded-[12px] shrink-0 w-[32px] h-[32px]">
                          <div className="basis-0 grow min-h-px min-w-px relative shrink-0 w-[24px]">
                            <svg
                              className="block w-full h-full"
                              fill="none"
                              preserveAspectRatio="none"
                              viewBox="0 0 24 32"
                            >
                              <g>
                                <path
                                  d="M18.0477 10.3808C18.3893 10.0395 18.9433 10.0396 19.285 10.3808C19.6267 10.7225 19.6267 11.2773 19.285 11.619L10.118 20.7851C9.95398 20.949 9.73171 21.0418 9.49982 21.0419C9.26791 21.0418 9.04468 20.9491 8.88068 20.7851L4.71369 16.619C4.37232 16.2774 4.3722 15.7224 4.71369 15.3808C5.0553 15.0392 5.61024 15.0394 5.95197 15.3808L9.49885 18.9286L18.0477 10.3808Z"
                                  fill="#0D0C07"
                                />
                              </g>
                            </svg>
                          </div>
                        </div>
                        <div className="flex flex-col gap-[4px] flex-1 items-start justify-center min-w-0">
                          <h3 className="font-bold leading-[32px] text-[20px] text-white mb-3 md:mb-1">
                            {t("landing.whatWeDo.feature2Title")}
                          </h3>
                          <p className="font-semibold leading-[24px] text-[#009900] text-[15.2px] mb-2">
                            {t("landing.whatWeDo.feature2Highlight")}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works Section (reuses already-translated copy) */}
        <section
          className={`bg-[#F3F3F2] pt-12 md:pt-20 pb-24 hero-element hero-how-it-works ${
            isExiting ? "exiting" : ""
          }`}
          style={{
            backgroundImage: "url('/assets/potato.png')",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "145% -1%",
            backgroundSize: "180px",
          }}
        >
          <div className="max-w-5xl mx-auto">
            <div className="px-10 md:px-6 pb-8">
              <div className="max-w-[280px] md:max-w-[100%]">
                <h2 className="font-bold text-[36px] md:text-[48px] text-[#0d0c07] md:leading-[72px] leading-[60px] tracking-[-0.1024px] mb-0">
                  {t("landing.howItWorks.title")}
                </h2>
                <p className="text-[15.2px] text-[#4d4b42] leading-[26px] mt-0">
                  {t("landing.howItWorks.subtitle")}
                </p>
              </div>
            </div>

            <div className="px-0 mb-6">
              <div className="space-y-6">
                <div className="flex gap-3 items-start px-10 md:px-6">
                  <div className="bg-[#ffd429] rounded-[12px] w-8 h-8 flex items-center justify-center flex-shrink-0">
                    <span className="font-semibold text-[14px] text-[#0d0c07] leading-[28px]">
                      1
                    </span>
                  </div>
                  <div>
                    <h3 className="font-bold text-[18px] text-[#0d0c07] leading-[32px] mb-1">
                      {t("landing.howItWorks.step1.title")}
                    </h3>
                    <p className="text-[14.8px] text-[#4d4b42] leading-[24px] mt-0">
                      {t("landing.howItWorks.step1.description")}
                    </p>
                  </div>
                </div>

                <div className="flex gap-3 items-start px-10 md:px-6">
                  <div className="bg-[#ffd429] rounded-[12px] w-8 h-8 flex items-center justify-center flex-shrink-0">
                    <span className="font-semibold text-[14px] text-[#0d0c07] leading-[28px]">
                      2
                    </span>
                  </div>
                  <div>
                    <h3 className="font-bold text-[18px] text-[#0d0c07] leading-[32px] mb-1">
                      {t("landing.howItWorks.step2.title")}
                    </h3>
                    <p className="text-[14.8px] text-[#4d4b42] leading-[24px] mt-0">
                      {t("landing.howItWorks.step2.description")}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="px-10 md:px-6 space-y-6">
              <div className="flex gap-3 items-start">
                <div className="bg-[#ffd429] rounded-[12px] w-8 h-8 flex items-center justify-center flex-shrink-0">
                  <span className="font-semibold text-[14px] text-[#0d0c07] leading-[28px]">
                    3
                  </span>
                </div>
                <div className="flex-1">
                  <div className="mb-5">
                    <h3 className="font-bold text-[18px] text-[#0d0c07] leading-[32px] mb-1">
                      {t("landing.howItWorks.step3.title")}
                    </h3>
                    <p className="text-[14.8px] text-[#4d4b42] leading-[24px] mt-0">
                      {t("landing.howItWorks.step3.description")}
                    </p>
                  </div>

                  <div className="bg-white rounded-[12px] overflow-hidden max-h-60 relative">
                    <div className="absolute inset-0 bg-gradient-to-b from-50% from-[#f3f3f200] to-[#f3f3f2] z-10 pointer-events-none"></div>
                    <div className="space-y-0">
                      <div className="flex gap-3 items-start p-3 border-b border-[#ebebea]/50">
                        <div className="w-20 h-20 bg-gray-200 rounded-[6px] flex-shrink-0">
                          <img
                            src="/assets/products/tomatoes.jpg"
                            alt={t("landing.groceryPreview.tomato")}
                            width="320"
                            height="320"
                            loading="lazy"
                            decoding="async"
                            className="w-full h-full object-cover rounded-[6px]"
                          />
                        </div>
                        <div className="flex-1 space-y-2">
                          <div>
                            <h4 className="font-semibold text-[14px] text-[#0d0c07] leading-[24px] mb-0">
                              {t("landing.groceryPreview.tomato")}
                            </h4>
                            <div className="flex gap-1 items-center">
                              <span className="font-semibold text-[12px] text-[#353227] leading-[20px]">
                                {t("landing.groceryPreview.units", {
                                  count: 2,
                                })}
                              </span>
                              <span className="text-[12px] text-[#0d0c07] leading-[20px]">
                                {t("landing.groceryPreview.approx", {
                                  amount: "220g",
                                })}
                              </span>
                            </div>
                          </div>
                          <div className="backdrop-blur-lg bg-[rgba(243,243,242,0.64)] px-2 py-1 rounded-[6px] border border-white/50 inline-block">
                            <div className="flex gap-1 items-center">
                              <span className="text-[#0d0c07] text-[12px] leading-[20px]">
                                {currencySymbol}1.20
                              </span>
                              <span className="text-[#a09f9c] text-[12px] leading-[20px]">
                                •
                              </span>
                              <span className="text-[#56554d] text-[12px] leading-[20px]">
                                {currencySymbol}0.60/
                                {t("landing.groceryPreview.perUnit")}
                              </span>
                            </div>
                          </div>
                          <span
                            aria-hidden="true"
                            className="flex w-[auto] select-none font-semibold text-[12px] text-[#0d0c07] leading-[20px] border-b border-[#ffcb00]"
                          >
                            {t("landing.groceryPreview.iHaveIt")}
                          </span>
                        </div>
                      </div>

                      <div className="flex gap-3 items-start p-3">
                        <div className="w-20 h-20 bg-gray-200 rounded-[6px] flex-shrink-0">
                          <img
                            src="/assets/products/avocados.jpg"
                            alt={t("landing.groceryPreview.avocado")}
                            width="320"
                            height="320"
                            loading="lazy"
                            decoding="async"
                            className="w-full h-full object-cover rounded-[6px]"
                          />
                        </div>
                        <div className="flex-1 space-y-2">
                          <div>
                            <h4 className="font-semibold text-[14px] text-[#0d0c07] leading-[24px] mb-0">
                              {t("landing.groceryPreview.avocado")}
                            </h4>
                            <span className="font-semibold text-[12px] text-[#353227] leading-[20px]">
                              {t("landing.groceryPreview.units", { count: 2 })}
                            </span>
                          </div>
                          <div className="backdrop-blur-lg bg-[rgba(243,243,242,0.64)] px-2 py-1 rounded-[6px] border border-white/50 inline-block">
                            <div className="flex gap-1 items-center">
                              <span className="text-[#0d0c07] text-[12px] leading-[20px]">
                                {currencySymbol}3.60
                              </span>
                              <span className="text-[#a09f9c] text-[12px] leading-[20px]">
                                •
                              </span>
                              <span className="text-[#56554d] text-[12px] leading-[20px]">
                                {currencySymbol}1.80/
                                {t("landing.groceryPreview.perUnit")}
                              </span>
                            </div>
                          </div>
                          <span
                            aria-hidden="true"
                            className="flex w-[auto] select-none font-semibold text-[12px] text-[#0d0c07] leading-[20px] border-b border-[#ffcb00]"
                          >
                            {t("landing.groceryPreview.iHaveIt")}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="w-full md:px-6 px-10 flex max-w-5xl pr-24 flex-col mb-2">
              <p className="font-bold text-[#0d0c07] text-[24px] leading-[32px] mt-10">
                {t("landingB.conclusion.thatsIt")}
              </p>
              <p className="font-bold text-[#0d0c07] text-[20px] leading-[28px] mt-1">
                {t("landingB.conclusion.noPlanning")}
              </p>
            </div>
          </div>
        </section>

        {/* FAQ Section (reuses already-translated copy) */}
        <section className="bg-white py-16 md:py-24">
          <div className="max-w-5xl mx-auto px-10 md:px-6">
            <div className="pb-8">
              <h2 className="font-bold text-[42px] md:text-[48px] text-[#0d0c07] leading-[72px] tracking-[-0.1024px] mb-0">
                {t("landing.faq.title")}
              </h2>
            </div>

            <div className="space-y-0">
              {[
                {
                  i: 0,
                  q: "q1",
                  body: (
                    <div className="space-y-3">
                      <p className="text-[15.2px] text-[#4d4b42] leading-[26px]">
                        <span className="font-semibold text-[#0d0c07]">
                          {t("landing.faq.a1_intro")}
                        </span>
                        <br />
                        {t("landing.faq.a1_desc")}
                      </p>
                      <ul className="text-[15.2px] text-[#4d4b42] leading-[26px] space-y-1">
                        {["a1_bullet1", "a1_bullet2", "a1_bullet3"].map((b) => (
                          <li key={b} className="flex items-start gap-2">
                            <span className="text-[#a09f9c]">•</span>
                            <span>{t(`landing.faq.${b}`)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ),
                },
                {
                  i: 1,
                  q: "q2",
                  body: (
                    <div className="space-y-3">
                      <p className="text-[15.2px] text-[#4d4b42] leading-[26px]">
                        {t("landing.faq.a2_p1")}
                      </p>
                      <p className="text-[15.2px] text-[#4d4b42] leading-[26px]">
                        {t("landing.faq.a2_p2")}
                      </p>
                    </div>
                  ),
                },
                {
                  i: 2,
                  q: "q3",
                  body: (
                    <div className="space-y-3">
                      <p className="text-[15.2px] text-[#4d4b42] leading-[26px]">
                        <span className="font-semibold text-[#0d0c07]">
                          {t("landing.faq.a3_intro")}
                        </span>
                      </p>
                      <p className="text-[15.2px] text-[#4d4b42] leading-[26px]">
                        {t("landing.faq.a3_desc")}
                      </p>
                    </div>
                  ),
                },
                {
                  i: 3,
                  q: "q4",
                  body: (
                    <div className="space-y-3">
                      <p className="text-[15.2px] text-[#4d4b42] leading-[26px]">
                        {t("landing.faq.a4_p1")}
                      </p>
                      <p className="text-[15.2px] text-[#4d4b42] leading-[26px]">
                        <span className="font-semibold text-[#0d0c07]">
                          {t("landing.faq.a4_intro")}
                        </span>
                      </p>
                      <ul className="text-[15.2px] text-[#4d4b42] leading-[26px] space-y-1">
                        {["a4_bullet1", "a4_bullet2", "a4_bullet3"].map((b) => (
                          <li key={b} className="flex items-start gap-2">
                            <span className="text-[#a09f9c]">•</span>
                            <span>{t(`landing.faq.${b}`)}</span>
                          </li>
                        ))}
                      </ul>
                      <p className="text-[15.2px] text-[#4d4b42] leading-[26px] font-medium">
                        {t("landing.faq.a4_conclusion")}
                      </p>
                    </div>
                  ),
                },
                {
                  i: 4,
                  q: "q5",
                  body: (
                    <div className="space-y-3">
                      <p className="text-[15.2px] text-[#4d4b42] leading-[26px]">
                        {t("landing.faq.a5_p1")}
                      </p>
                      <p className="text-[15.2px] text-[#4d4b42] leading-[26px]">
                        {t("landing.faq.a5_p2")}
                      </p>
                      <p className="text-[15.2px] text-[#006600] leading-[26px] font-semibold">
                        {t("landing.faq.a5_highlight", { currencySymbol })}
                      </p>
                    </div>
                  ),
                },
                {
                  i: 5,
                  q: "q6",
                  body: (
                    <div className="space-y-3">
                      <p className="text-[15.2px] text-[#4d4b42] leading-[26px]">
                        <span className="font-semibold text-[#0d0c07]">
                          {t("landing.faq.a6_intro")}
                        </span>
                      </p>
                      <p className="text-[15.2px] text-[#4d4b42] leading-[26px]">
                        {t("landing.faq.a6_p1")}
                      </p>
                      <p className="text-[15.2px] text-[#4d4b42] leading-[26px]">
                        {t("landing.faq.a6_p2")}
                      </p>
                    </div>
                  ),
                },
                {
                  i: 6,
                  q: "q7",
                  body: (
                    <div className="space-y-3">
                      <p className="text-[15.2px] text-[#4d4b42] leading-[26px]">
                        {t("landing.faq.a7_p1")}
                      </p>
                      <p className="text-[15.2px] text-[#4d4b42] leading-[26px] font-medium italic">
                        {t("landing.faq.a7_p2")}
                      </p>
                    </div>
                  ),
                },
              ].map(({ i, q, body }) => (
                <div key={q} className="border-b border-[#ebebea]">
                  <button
                    className="w-full py-5 flex justify-between items-center text-left rounded-[6px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#0d0c07]"
                    onClick={() => toggleFaq(i)}
                    aria-expanded={openFaqIndex === i}
                    aria-controls={`faq-panel-${i}`}
                    id={`faq-trigger-${i}`}
                  >
                    <h3 className="font-bold text-[18px] text-[#0d0c07] leading-[32px]">
                      {t(`landing.faq.${q}`)}
                    </h3>
                    <svg
                      className={`w-6 h-6 shrink-0 text-[#0d0c07] transition-transform duration-300 motion-reduce:transition-none ${
                        openFaqIndex === i ? "rotate-180" : ""
                      }`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                  <div
                    id={`faq-panel-${i}`}
                    role="region"
                    aria-labelledby={`faq-trigger-${i}`}
                    aria-hidden={openFaqIndex !== i}
                    className={`grid transition-all duration-300 motion-reduce:transition-none ${
                      openFaqIndex === i
                        ? "grid-rows-[1fr] pb-5"
                        : "grid-rows-[0fr]"
                    }`}
                  >
                    <div className="overflow-hidden">{body}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Reviews Section */}
        <section
          aria-labelledby="testb-reviews-heading"
          className={`relative overflow-hidden hero-element hero-reviews ${
            isExiting ? "exiting" : ""
          }`}
          style={{
            backgroundImage: `url('/assets/landing/hero-bg.jpg')`,
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            backgroundSize: "cover",
          }}
        >
          {/* Dark overlay for legible text over the photo */}
          <div
            className="absolute inset-0 bg-[#0d0c07]/90 pointer-events-none"
            aria-hidden="true"
          ></div>

          <div className="relative max-w-5xl mx-auto px-10 md:px-6 py-20 md:py-28">
            <div className="max-w-2xl mb-10">
              <h2
                id="testb-reviews-heading"
                className="font-extrabold text-[32px] md:text-[40px] text-white leading-[40px] md:leading-[48px] tracking-[-0.32px] mb-3"
              >
                {t("landingB.reviews.title")}
              </h2>
              <p className="text-[16px] text-[#d8d6cf] leading-[28px]">
                {t("landingB.reviews.subtitle")}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {reviews.map((r, i) => (
                <figure
                  key={i}
                  className="flex flex-col gap-3 p-5 rounded-[12px] border-[0.5px] border-white/15 bg-white/10 backdrop-blur-lg shadow-[0px_6px_12px_0px_rgba(0,0,0,0.18)]"
                >
                  <span
                    className="flex gap-0.5"
                    role="img"
                    aria-label={t("landingB.reviews.rating")}
                  >
                    {[0, 1, 2, 3, 4].map((s) => (
                      <svg
                        key={s}
                        width="16"
                        height="16"
                        viewBox="0 0 20 20"
                        fill="#FFCB00"
                        aria-hidden="true"
                      >
                        <path d="M10 1.5l2.6 5.27 5.82.85-4.21 4.1.99 5.78L10 14.77l-5.2 2.73.99-5.78L1.58 7.62l5.82-.85L10 1.5z" />
                      </svg>
                    ))}
                  </span>
                  <blockquote className="text-[15px] text-white leading-[24px] flex-1">
                    “{r.quote}”
                  </blockquote>
                  <figcaption className="text-[13px] text-[#d8d6cf] leading-[20px]">
                    <span className="font-semibold text-white">{r.name}</span>
                    {r.context ? <span> · {r.context}</span> : null}
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        <div ref={footerRef}>
          <Footer />
        </div>

        {/* Fixed Bottom CTA */}
        <div
          className={`fixed-bottom-cta ${
            showFixedBottomCta && !ctaHiddenByFooter ? "show" : "hide"
          }`}
        >
          <button
            className="
              inline-flex justify-center items-center gap-1
              h-[56px] px-[40px] py-[14px] mb-4
              rounded-[32px]
              border-[1px] border-[linear-gradient(200deg,_#090_0%,_#060_100%)]
              bg-[#090]
              text-[#FFF] text-center
              text-[14px] font-medium leading-[28px]
              transition-colors
              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#0d0c07]
              md:hover:border-[0.5px] md:hover:border-[#060]
              md:hover:bg-[#060]
              bg-[linear-gradient(200deg,_#090_0%,_#060_100%)]
              shadow-[0_2px_16px_0_rgba(0,0,0,0.12)_inset,_0_0_32px_0_rgba(255,255,255,0.4)_inset,_0_0px_0px_2px_rgba(0,102,0,0.4),_0_-4px_16px_0px_rgba(0,0,0,0.2)]
            "
            onClick={() => handleStart(CTA_LOCATION.STICKY)}
          >
            <span>{t("landingB.hero.cta")}</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M11.3816 5.54742C11.702 5.22734 12.2089 5.20753 12.5525 5.48785L12.6189 5.54742L18.4519 11.3814C18.7936 11.7231 18.7936 12.277 18.4519 12.6187L12.6189 18.4517L12.5525 18.5123C12.2088 18.7925 11.7019 18.7721 11.3816 18.4517C11.0399 18.11 11.0399 17.5561 11.3816 17.2144L15.7214 12.8746H6.16675C5.6835 12.8746 5.29175 12.4828 5.29175 11.9996C5.29192 11.5165 5.68361 11.1246 6.16675 11.1246H15.7205L11.3816 6.7857L11.321 6.7193C11.0406 6.37562 11.0612 5.86782 11.3816 5.54742Z"
                fill="white"
              />
            </svg>
          </button>
        </div>
      </div>
    </>
  );
};

export default LandingPage;
